import type { DebouncedFunc } from 'lodash';
import _debounce from 'lodash/debounce';
import type { ObjectDirective } from 'vue';

/**
 * Toggles the given class on a `position: sticky` element while it is pinned to its `top` offset.
 *
 * The element is observed against a viewport shrunk by (sticky offset + 1px) at the top: while scrolling normally it
 * fully intersects, but as soon as it sticks its top edge sits inside that margin and the observer reports a partial
 * intersection. The extra checks rule out an element that is only clipped at the bottom of the viewport.
 *
 * Usage: v-sticky-stuck="'et-component__element--stuck'"
 */
const observers = new WeakMap<HTMLElement, IntersectionObserver>();
const resizeHandlers = new WeakMap<HTMLElement, DebouncedFunc<() => void>>();

export const StickyStuck: ObjectDirective<HTMLElement, string> = {
    mounted(el, binding) {
        const stuckClass = binding.value;

        if (!stuckClass) {
            return console.warn("Warning! No class name on 'v-sticky-stuck' directive!");
        }

        if (!window.IntersectionObserver) {
            return;
        }

        observe(el, stuckClass);

        // The sticky offset (and whether the element is sticky at all) can change with the viewport width
        const handleResize = _debounce(() => observe(el, stuckClass), 100);
        resizeHandlers.set(el, handleResize);
        window.addEventListener('resize', handleResize);
    },
    beforeUnmount(el) {
        observers.get(el)?.disconnect();
        observers.delete(el);

        const handleResize = resizeHandlers.get(el);

        if (handleResize) {
            handleResize.cancel();
            window.removeEventListener('resize', handleResize);
            resizeHandlers.delete(el);
        }
    },
};

function observe(element: HTMLElement, stuckClass: string) {
    observers.get(element)?.disconnect();

    const stickyOffset = getStickyOffset(element);
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const isStuck =
                    getComputedStyle(element).position === 'sticky' &&
                    entry.intersectionRatio < 1 &&
                    entry.boundingClientRect.top <= stickyOffset + 1;

                element.classList.toggle(stuckClass, isStuck);
            });
        },
        { threshold: [1], rootMargin: `-${stickyOffset + 1}px 0px 0px 0px` },
    );

    observer.observe(element);
    observers.set(element, observer);
}

function getStickyOffset(element: HTMLElement): number {
    const top = parseFloat(getComputedStyle(element).top);

    return Number.isNaN(top) ? 0 : top;
}
